import Typography from '@ixigo/iui/Typography';
import type { VehicleType } from '@/api';
import { Capacity, type CapacityProps } from './Capacity';

export interface SummaryProps extends CapacityProps {
	/** The vehicleType of the cab (e.g., "SUV", "Hatchback") */
	vehicleType: VehicleType;
	/** A short description of the cab vehicleType (e.g., "Creta or similar") */
	model: string;
	/** Cab partner */
	cabPartner?: string;
}

/**
 * Summary component displays vehicle information and pricing details
 *
 * @param vehicleType - Type of vehicle (e.g., "SUV", "Hatchback")
 * @param model - Vehicle model description (e.g., "Creta or similar")
 * @param capacityData - Capacity information with passenger and luggage counts
 * @returns JSX element with vehicle details and pricing summary
 *
 *
 * Possible errors:
 * - Missing vehicleType or model will display empty elements
 *
 * Exception handling:
 * - Gracefully handles missing capacity properties
 */
export const Summary = ({
	vehicleType,
	model,
	cabPartner,
	...capacity
}: SummaryProps) => {
	const modelText = cabPartner ? `${model} | Operated by ${cabPartner}` : model;
	return (
		<div className="flex flex-col gap-y-0.5 size-full overflow-hidden">
			<div className="flex items-start justify-between flex-1">
				<Typography variant="body-md" fontWeight="medium">
					{vehicleType}
				</Typography>
				<Capacity {...capacity} />
			</div>
			<Typography variant="body-sm" className="truncate">
				{modelText}
			</Typography>
		</div>
	);
};
